import { KeyRound, Plus, ShieldAlert } from "lucide-react";
import { useEffect, useState, type FormEvent } from "react";
import { api } from "../../lib/api";
import { fmt, when } from "../../lib/format";
import { keys as queryKeys, useAction, useKeys } from "../../lib/queries";
import type { ApiKey, Project } from "../../lib/types";
import { Badge } from "../../ui/Badge";
import { Button } from "../../ui/Button";
import { Card } from "../../ui/Card";
import { Dialog } from "../../ui/Dialog";
import { EmptyState, useToast } from "../../ui/Feedback";
import { ChipsInput, Field, FormGrid, Input, Select } from "../../ui/Form";
import { CodeBlock } from "../../ui/Misc";
import table from "../../ui/Table.module.css";
import styles from "./SettingsTab.module.css";

/**
 * Site keys (pk_) live in page HTML and only work from listed origins.
 * Secret keys (sk_) are server credentials, shown in plaintext exactly once:
 * after that only their hash exists (Ketentuan Penggunaan API Pasal 3). They
 * come in two scopes: the full API for Dipandu integrations, and link sync
 * only, for a Web Dinamis server that pushes catalogue changes to Linkage.
 */
export function KeysPanel({ project }: { project: Project }) {
  const list = useKeys(project.id);
  const toast = useToast();
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<ApiKey | null>(null);
  const [revealed, setRevealed] = useState<{ plaintext: string; key: ApiKey } | null>(null);
  const invalidate = [queryKeys.keys(project.id), queryKeys.project(project.id)];
  const revoke = useAction((id: string) => api.post(`/projects/${project.id}/keys/${id}/revoke`), invalidate);

  return (
    <Card
      title="Kunci API & domain"
      description="Widget memakai kunci situs. Integrasi API (Dipandu) dan sinkron rujukan Linkage memakai kunci rahasia dari server klien."
      flush
      actions={
        <Button size="sm" icon={<Plus aria-hidden />} onClick={() => setCreating(true)}>
          Kunci baru
        </Button>
      }
    >
      {!list.data?.length ? (
        <EmptyState icon={<KeyRound aria-hidden />} title="Belum ada kunci" />
      ) : (
        <div className={table.wrap}>
          <table className={table.table}>
            <thead>
              <tr>
                <th className={table.first}>Kunci</th>
                <th>Jenis</th>
                <th>Domain diizinkan</th>
                <th>Terakhir dipakai</th>
                <th className={table.last} />
              </tr>
            </thead>
            <tbody>
              {list.data.map((key) => (
                <tr key={key.id} style={{ opacity: key.revokedAt ? 0.5 : 1 }}>
                  <td className={table.first}>
                    <div style={{ fontWeight: 600 }}>{key.label}</div>
                    <code className={table.muted}>{key.kind === "site" ? `${key.display.slice(0, 18)}…` : key.display}</code>
                  </td>
                  <td>
                    <Badge tone={key.kind === "secret" ? "serious" : "accent"}>{key.kind === "secret" ? (key.scope === "links" ? "sinkron rujukan" : "rahasia") : "situs"}</Badge> <Badge>{key.env === "live" ? "produksi" : "uji coba"}</Badge>
                    {key.env === "test" && (
                      <div className={table.muted}>
                        terpakai {fmt.rupiah(key.testSpentMilli)} / {fmt.rupiah(key.testBudgetMilli)}
                      </div>
                    )}
                  </td>
                  <td className={table.muted} style={{ maxWidth: 280 }}>
                    {key.kind === "secret" ? "server ke server (tanpa Origin)" : key.allowedOrigins.join(", ")}
                  </td>
                  <td className={table.muted}>{key.revokedAt ? `dicabut ${when.ago(key.revokedAt)}` : when.ago(key.lastUsedAt)}</td>
                  <td className={table.last}>
                    {!key.revokedAt && (
                      <div style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}>
                        {key.kind === "site" && (
                          <Button size="sm" variant="ghost" onClick={() => setEditing(key)}>
                            Domain
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="danger"
                          onClick={async () => {
                            if (!confirm(`Cabut kunci “${key.label}”? Semua permintaan dengan kunci ini langsung ditolak.`)) return;
                            await revoke.mutateAsync(key.id);
                            toast("Kunci dicabut.");
                          }}
                        >
                          Cabut
                        </Button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <CreateKeyDialog
        project={project}
        open={creating}
        onClose={() => setCreating(false)}
        onCreated={(result) => {
          setCreating(false);
          setRevealed(result);
        }}
      />
      <OriginsDialog projectId={project.id} apiKey={editing} onClose={() => setEditing(null)} />
      <Dialog
        open={!!revealed}
        onClose={() => setRevealed(null)}
        title={revealed?.key.kind === "secret" ? "Simpan kunci rahasia ini sekarang" : "Kunci situs dibuat"}
        description={revealed?.key.kind === "secret" ? "Kunci ini tidak akan ditampilkan lagi. Kirim lewat kanal aman ke engineer klien." : "Tempel kode di bawah sebelum </body> di website klien."}
        footer={<Button variant="primary" onClick={() => setRevealed(null)}>Sudah saya simpan</Button>}
      >
        {revealed &&
          (revealed.key.kind === "secret" ? (
            <div style={{ display: "grid", gap: 12 }}>
              <CodeBlock code={revealed.key.scope === "links" ? `NAVIBIZ_LINKS_KEY=${revealed.plaintext}\nNAVIBIZ_CHAT_API_URL=${location.origin}` : revealed.plaintext} />
              {revealed.key.scope === "links" && <p style={{ margin: 0 }}>Tempel di env server Web Dinamis klien. Setiap simpan produk di panel admin akan memperbarui rujukan.</p>}
              <p className={styles.error} style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 0 }}>
                <ShieldAlert size={16} aria-hidden /> Hanya di server. Jangan pernah di kode frontend, aplikasi seluler, atau repositori publik.
              </p>
            </div>
          ) : (
            <CodeBlock code={`<script async src="${location.origin}/widget.js" data-key="${revealed.plaintext}"></script>`} />
          ))}
      </Dialog>
    </Card>
  );
}

function CreateKeyDialog({ project, open, onClose, onCreated }: { project: Project; open: boolean; onClose: () => void; onCreated: (result: { plaintext: string; key: ApiKey }) => void }) {
  const api_ = project.product === "ai-p" || project.product === "ai-pl";
  const [kind, setKind] = useState<"site" | "secret" | "links">("site");
  const [env, setEnv] = useState<"live" | "test">("live");
  const [origins, setOrigins] = useState<string[]>(project.websiteUrl ? [new URL(project.websiteUrl).origin] : []);
  const create = useAction((body: Record<string, unknown>) => api.post<{ plaintext: string; key: ApiKey }>(`/projects/${project.id}/keys`, body), [queryKeys.keys(project.id)]);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const result = await create.mutateAsync({
      kind: kind === "links" ? "secret" : kind,
      scope: kind === "links" ? "links" : "all",
      env,
      label: form.get("label"),
      allowedOrigins: kind === "site" ? origins : [],
      testBudgetRupiah: Number(form.get("budget") ?? 25000),
    });
    onCreated(result);
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Kunci API baru"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Batal
          </Button>
          <Button type="submit" form="new-key" variant="primary" loading={create.isPending}>
            Buat kunci
          </Button>
        </>
      }
    >
      <form id="new-key" onSubmit={submit} style={{ display: "grid", gap: 16 }}>
        <FormGrid>
          <Field label="Jenis">
            {(id) => (
              <Select id={id} value={kind} onChange={(e) => setKind(e.target.value as "site" | "secret" | "links")}>
                <option value="site">Kunci situs (widget)</option>
                <option value="secret" disabled={!api_}>
                  Kunci rahasia: chat API (Dipandu)
                </option>
                <option value="links" disabled={!project.linkage.enabled}>
                  Kunci rahasia: sinkron rujukan saja (Linkage)
                </option>
              </Select>
            )}
          </Field>
          <Field label="Lingkungan">
            {(id) => (
              <Select id={id} value={env} onChange={(e) => setEnv(e.target.value as "live" | "test")}>
                <option value="live">Produksi — memotong saldo klien</option>
                <option value="test">Uji coba — anggaran sendiri</option>
              </Select>
            )}
          </Field>
        </FormGrid>
        <Field label="Label">{(id) => <Input id={id} name="label" required minLength={2} placeholder={kind === "secret" ? "Server staging" : kind === "links" ? "Sinkron panel admin" : "Widget website"} />}</Field>
        {kind === "site" && (
          <Field label="Domain yang diizinkan" hint="Skema + domain, tanpa path. Boleh https://*.domain.com dan http://localhost:*">
            {(id) => <ChipsInput id={id} value={origins} onChange={setOrigins} placeholder="https://toko.biz.id" />}
          </Field>
        )}
        {env === "test" && (
          <Field label="Anggaran uji coba (Rp)" hint="Tidak memotong saldo klien. Lampiran Ketentuan API: Rp 25.000.">
            {(id, describedBy) => <Input id={id} name="budget" type="number" min={0} max={500000} defaultValue={25000} aria-describedby={describedBy} />}
          </Field>
        )}
        {create.isError && <p className={styles.error}>{create.error.message}</p>}
      </form>
    </Dialog>
  );
}

function OriginsDialog({ projectId, apiKey, onClose }: { projectId: string; apiKey: ApiKey | null; onClose: () => void }) {
  const toast = useToast();
  const [origins, setOrigins] = useState<string[]>([]);
  useEffect(() => {
    setOrigins(apiKey?.allowedOrigins ?? []);
  }, [apiKey]);
  const save = useAction((list: string[]) => api.patch(`/projects/${projectId}/keys/${apiKey!.id}`, { allowedOrigins: list }), [queryKeys.keys(projectId)]);
  return (
    <Dialog
      open={!!apiKey}
      onClose={onClose}
      title={`Domain untuk “${apiKey?.label ?? ""}”`}
      description="Widget hanya mau bekerja dari domain ini."
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Batal
          </Button>
          <Button
            variant="primary"
            loading={save.isPending}
            onClick={async () => {
              await save.mutateAsync(origins);
              toast("Domain diperbarui.");
              onClose();
            }}
          >
            Simpan
          </Button>
        </>
      }
    >
      <ChipsInput value={origins} onChange={setOrigins} placeholder="https://toko.biz.id" />
      {save.isError && <p className={styles.error}>{save.error.message}</p>}
    </Dialog>
  );
}

